import { Box, Stack } from "@mui/material";
import Head from "next/head";
import { GetStaticPaths, GetStaticProps } from "next";
import React from "react";

import {
  ParlorHeadSection,
  ATypeCardView,
  ATypeFacilityMainHead,
  ATypeFacilityPriceSummary,
  DefaultFacility,
  BTypeBreadCrumb,
  GOI_WEB_URL,
  DTypeTabsFacility,
  ATypeFacilityList,
  ClickEventType,
  useFacilities,
} from "goi_common";
import facilities from "goi_common/src/constants/facilities_detail.json";

import { parlorRegion } from "@/constants/parlorRegion";

const { regionColor, targetSidos } = parlorRegion;

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = facilities
    .filter((facility) => targetSidos.includes(facility.sido))
    .map((facility) => ({
      params: {
        id: String(facility.id),
      },
    }));

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const id = params?.id as string;

  const p_facility = facilities.find(
    (facility) => Number(facility.id) === Number(id)
  ) as DefaultFacility;

  const { address, companyname, images, hallrents } = p_facility;

  const 시가아닌_도인경우 = address.split(" ")[0].length == 4;
  const district = 시가아닌_도인경우
    ? address[0] + address[2]
    : address.slice(0, 2);

  const firstHallrent = hallrents[0] || { item: "", detail: "", charge: "" };
  const lastHallrent = hallrents.at(-1) || { item: "", detail: "", charge: "" };

  // hallerts에서 찾아보기 넣을 만한거
  const description = `${companyname}의 시설은 ${firstHallrent.item} ${
    firstHallrent.detail
  } ${
    firstHallrent.charge && firstHallrent.charge.toLocaleString() + "원"
  }부터 ${lastHallrent.item} ${lastHallrent.detail} ${
    lastHallrent.charge && lastHallrent.charge.toLocaleString() + "원"
  }까지 분포되어 있습니다.`;

  const pageKeywords = `
    ${district} 납골당,
    ${district} 납골당 비용,
    ${district} 봉안당 비용,
    ${district} 장지 비용,
    ${district} 수목장 비용,
    ${companyname},
    ${companyname} 비용,
    ${companyname} 가격,
    ${companyname} 시설비용,
    ${companyname} 안치비용,
    ${companyname} 비용 혜택,
    ${companyname} 안치조건,
    ${companyname} 국가유공자 혜택,
    ${companyname} 기초생활수급자 혜택,
    ${companyname} 공무원 혜택,
    `;

  return {
    props: {
      defaultFacility: p_facility,
      pageDescription: description,
      pageKeywords: pageKeywords,
      pageOgTitle: `[${district}] ${companyname} 비용 알아보기 | 대구경북장례정보포털`,
      pageOgDescription: description,
      pageOgImage: images[0]?.url || "",
    },
  };
};

interface PriceProps {
  defaultFacility: DefaultFacility;
  pageOgTitle: string;
}

export default function Price({ defaultFacility, pageOgTitle }: PriceProps) {
  const currentPageEvent: ClickEventType = {
    domain: "dggb",
    path: "facility_price",
  };

  return (
    <>
      <Head>
        <title>{pageOgTitle}</title>
        {/* {regionParlors.length > 5 && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(carouselRichSearch),
            }}
          />
        )} */}
      </Head>

      <ParlorHeadSection
        component={
          <>
            <BTypeBreadCrumb
              firstItem={{
                text: `장지 추천`,
                href: `${GOI_WEB_URL}/match/facility-requests/${defaultFacility.id}/`,
                event: {
                  ...currentPageEvent,
                  trigger_and_target: "br_crumb_mth_facil_req",
                },
              }}
            />
            <ATypeFacilityMainHead
              defaultFacility={defaultFacility}
              title={`${defaultFacility.companyname} 비용`}
            />
          </>
        }
      />

      <ParlorPriceContentSection
        defaultFacility={defaultFacility}
        event={currentPageEvent}
      />
    </>
  );
}

interface ParlorPriceContentSectionProps {
  defaultFacility: DefaultFacility;
  event: ClickEventType;
}

const ParlorPriceContentSection = ({
  defaultFacility,
  event,
}: ParlorPriceContentSectionProps) => {
  const { facilityList, facilityPrices } = useFacilities({
    sido: defaultFacility.sido,
  });

  return (
    <>
      <DTypeTabsFacility
        href={`facility-place/${defaultFacility.id}`}
        mainColor={regionColor}
        selectTab="비용"
      />

      <Stack style={{ background: regionColor.c100 }} padding="20px 34px">
        <ATypeFacilityPriceSummary
          defaultFacility={defaultFacility}
          mainColor={regionColor}
          detailButtonEvent={event}
        />
      </Stack>

      <Box marginTop="40px" />

      <ATypeFacilityList title="주변 장지 비용">
        {facilityList.length > 0 &&
          facilityList?.map((res, index) => {
            const { max, min } = facilityPrices[index];
            const averagePrice = (max + min) / 2;

            return (
              <ATypeCardView
                key={res.companyname}
                name={res.companyname}
                href={`/facility-place/${res.id}/price/`}
                arrowText={`${res.companyname} 납골당 비용 알아보기`}
                descriptions={[
                  `장지 평균 비용 : ${averagePrice.toLocaleString()}원`,
                ]}
                imgSrc={res.images?.[0].url || ""}
                event={{
                  ...event,
                  trigger_and_target: "uv_facility_price",
                }}
              />
            );
          })}
      </ATypeFacilityList>
    </>
  );
};
